import { newGuid } from "../../../util/types/guid";
import { ISessionStore } from "../../session/SessionStore";
import { ISystemDefaultsStore } from "../../system-defaults/SystemDefaultsStore";
import { action, computed, makeObservable, observable } from "mobx";
import { DobcoSocket, IDobcoSocket } from "./DobcoSocket";
import { ITblHomerPairing } from "../../../app/viewer/network/contracts/db/maindb/tblHomerPairing";
import { HomerInfoSection } from "./HomerInfoSection";
import { BrowserDetector } from "../../../util/browser/BrowserDetector";
import { GetAppVersionInfoResponse } from "../../../network/responses/GetAppVersionInfoResponse";
import { UserInfoModel } from "../../../../OpenApi/api";
import { notNullOrUndefined } from "../../../util/functions/notNullOrUndefined";
import { IHomeReadingFetcher } from "../../../network/fetchers/HomeReadingFetcher";
import { ISet, SetFactory } from "../../../util/collections/Set";

export interface IHomeReadingPairingStore {
	enabled: boolean;
	open: boolean;
	maxPairingAge: number;
	defaultLanguage: string | null;
	possibleLanguages: ISet<string>;
	qrCodeSource: string;
	currentPairing: ITblHomerPairing | null;
	activeInfoSection: HomerInfoSection;
	socketOpen: boolean;
	phoneConnected: boolean;
	showUpdateInfo: boolean;
	powHomerBGuid: string | null;
	browserKey: string;
	dobcoSocket: IDobcoSocket | null;
	pageId: string;
	tblUserUid: number | null;
	pairRequestData: any;
	newRequestTimer: number;

	initialize(): void;
	setOpen(open: boolean): void;
	acceptPairingRequest(): Promise<void>;
	denyPairingRequest(): void;
	unpairConnectionFromBrowser(): Promise<void>;
	changeLanguageFromBrowser(language: string): void;
	on(event: IHomeReadingInterfaceEvents, callback: Function): void;
}

export class HomeReadingPairingStore implements IHomeReadingPairingStore {
	@observable
	public enabled: boolean = false;

	@observable
	public open: boolean = false;

	@observable
	private webSocketAddress: string = "";

	@observable
	private currentWebSocketSecure: string = "";

	@observable
	private currentWebSocketAddress: string = "";

	@observable
	private currentWebSocketPort: string = "";

	@observable
	public pageId: string = "";

	@observable
	public powHomerBGuid: string | null = null;

	@observable
	public dobcoSocket: IDobcoSocket | null = null;

	@observable
	public defaultLanguage: string | null = null;

	public possibleLanguages: ISet<string> = SetFactory.createNew();

	@observable
	public currentPairing: ITblHomerPairing | null = null;

	private events: any = {};

	@observable
	public socketOpen: boolean = false;

	@observable
	public phoneConnected: boolean = false;

	@observable
	public activeInfoSection: HomerInfoSection = "qr";

	@observable
	private gettingVersionInfo: boolean = false;

	@observable
	private appVersion: number = 10000;

	@observable
	private minVersion: number = 0;

	@observable
	private currentVersion: number = 0;

	@observable
	public newRequestTimer: number = 0;

	@observable
	public showUpdateInfo: boolean = false;

	@observable
	private newRequestTimeout: NodeJS.Timeout | null = null;

	private readonly newRequestTimeoutInSeconds = 30;

	@observable
	private newRequestInterval: NodeJS.Timeout | null = null;

	@observable
	private readonly checkConnectionInterval: NodeJS.Timeout | null = null;

	@observable
	public pairRequestData: any = null;

	constructor(
		private readonly systemDefaultsStore: ISystemDefaultsStore,
		private readonly sessionStore: ISessionStore,
		private readonly homeReadingFetcher: IHomeReadingFetcher,
	) {
		makeObservable(this);
	}

	@action
	public initialize() {
		if (!this._currentUser) return;

		const settings = this._currentUser.settings?.homeReadingSettings;
		if (!settings?.enableHomeReading) return;

		this.webSocketAddress = settings.homeReadingWebSocketAddress ?? "";
		if (!this.webSocketAddress) {
			console.warn("Homer - No websocket address defined!");
			return;
		}

		if (BrowserDetector.isThisMobile()) {
			console.warn("Homer - Not supported on mobile devices");
			return;
		}

		this.enabled = true;

		this.determineWebSocketAddressData();
		this.determinePowHomerBGuid();
		this.renewPageId();
		this.initializeSocket();

		this.possibleLanguages = SetFactory.create(settings.languages);
		if (settings.language) {
			this.possibleLanguages.add(settings.language);
			this.setDefaultLanguage(settings.language);
		}

		this.getPairings();
	}

	@action
	private initializeSocket() {
		try {
			const self = this;
			this.dobcoSocket = new DobcoSocket(
				this.webSocketAddress,
				this.powHomerBGuid ?? "",
				this.pageId,
				function () {
					self.trigger("registered", {});
				},
				this._currentUser?.id ?? 0,
			);

			this.dobcoSocket.onOpen = function () {
				self.setSocketOpen(true);
				self.checkIfPhoneConnected();

				if (self.currentPairing == null) {
					self.getPairings(() => {
						self.initialSetPageActive();
					});
				} else {
					self.initialSetPageActive();
				}
			};

			this.dobcoSocket.onClose = function () {
				self.setSocketOpen(false);
				self.setPhoneConnectedStatus(false);
			};

			this.dobcoSocket.onError = function () {
				self.setSocketOpen(false);
				self.setPhoneConnectedStatus(false);
			};

			//Other callbacks are registered at the HomeReadingStore.ts
			this.dobcoSocket.registerCallback("pairRequest", function (data) {
				self.newPairingRequest(data);
			});
			this.dobcoSocket.registerCallback("phoneConnectedChanged", function (data) {
				self.setPhoneConnectedStatus(data.connected);
			});
			this.dobcoSocket.registerCallback("unpair", function () {
				self.unpairConnectionFromPhone();
			});
			this.dobcoSocket.registerCallback("pair", function () {
				self.pairConnectionFromPhone();
			});
			this.dobcoSocket.registerCallback("languageChanged", function (data) {
				self.changeLanguageFromPhone(data);
			});
			this.dobcoSocket.registerCallback("registerPhone", function () {
				/* CALL STOPS AT WS SERVER */
			});
			this.dobcoSocket.registerCallback("isBrowserConnected", function () {
				/* CALL STOPS AT WS SERVER */
			});
			this.dobcoSocket.registerCallback("getConfiguration", function () {
				/* CALL STOPS AT WS SERVER */
			});
		} catch (message) {
			console.error("DobcoSocket could not be created", message);
		}
	}

	@action
	public setSocketOpen(value: boolean) {
		this.socketOpen = value;
	}

	@computed
	public get browserKey(): string {
		return this.powHomerBGuid + "_" + (this._currentUser?.id ?? 0);
	}

	@computed
	private get _currentUser(): UserInfoModel | null {
		return this.sessionStore?.currentUser ?? null;
	}

	@action
	private determineWebSocketAddressData() {
		const arrayFullWebSocketAddress = this.webSocketAddress.split("/");
		if (arrayFullWebSocketAddress[0] !== undefined) {
			const splitWebSocketSecure = arrayFullWebSocketAddress[0].split(":");
			if (splitWebSocketSecure[0] !== undefined) {
				this.currentWebSocketSecure = splitWebSocketSecure[0];
			}
		}
		const fullWebSocketAddress = arrayFullWebSocketAddress[2];
		if (fullWebSocketAddress !== undefined) {
			const splitFullWebSocketAddress = fullWebSocketAddress.split(":");
			this.currentWebSocketAddress = splitFullWebSocketAddress[0];
			if (splitFullWebSocketAddress[1] !== undefined) {
				this.currentWebSocketPort = splitFullWebSocketAddress[1];
			}
		}
	}

	private determinePowHomerBGuid() {
		this.powHomerBGuid = localStorage.getItem("POWHomerBGuid");
		if (!this.powHomerBGuid) {
			this.powHomerBGuid = newGuid();
			localStorage.setItem("POWHomerBGuid", this.powHomerBGuid);
		}
	}

	@action
	public setCurrentPairing(pairing: ITblHomerPairing | null) {
		this.currentPairing = pairing;
	}

	@action
	private async getPairings(callback?: Function) {
		const result = await this.homeReadingFetcher.getPairingsByBrowserKey(this.browserKey);
		if (result.allOk) {
			if (result.pairings != null && result.pairings.length > 0) {
				this.setCurrentPairing(result.pairings[0]);
				this.setActiveInfoSection("connection");
				this.checkIfPhoneConnected();
				if (callback != null && typeof callback !== "undefined") {
					callback();
				}
			} else {
				this.setActiveInfoSection("qr");
			}
		} else {
			this.setActiveInfoSection("qr");
		}
	}

	@action
	private setActiveInfoSection(activeInfo: HomerInfoSection) {
		this.activeInfoSection = activeInfo;
	}

	@action
	private setPhoneConnected(value: boolean) {
		this.phoneConnected = value;
	}

	@action
	private checkIfPhoneConnected() {
		const self = this;

		if (this.currentPairing != null) {
			const passObject = {
				bAuth: {
					browserKey: this.browserKey,
					pguid: this.currentPairing.pGuid,
					pairCode: this.currentPairing.pairCode,
				},
			};
			if (this.socketOpen && this.dobcoSocket) {
				this.dobcoSocket.send("isPhoneConnected", passObject, function (data) {
					self.setPhoneConnectedStatus(data.connected);
				});
			} else {
				self.setPhoneConnectedStatus(false);
			}
		} else {
			self.setPhoneConnected(false);
		}
	}

	@action
	private setPhoneConnectedStatus(connected: boolean) {
		if (this.currentPairing != null) {
			if (this.phoneConnected !== connected) {
				this.setPhoneConnected(connected);
				this.trigger("PhoneConnectionChanged", connected);
				if (connected) {
					this.checkAppVersion();
				}
			}
		} else if (this.phoneConnected !== connected) {
			this.setPhoneConnected(connected);
			this.trigger("PhoneConnectionChanged", connected);
		}
	}

	@action
	private async checkAppVersion() {
		if (!this.currentPairing || this.gettingVersionInfo) return;

		this.gettingVersionInfo = true;
		const result: GetAppVersionInfoResponse = await this.homeReadingFetcher.getAppVersionInfo();
		this.gettingVersionInfo = false;

		if (result.allOk) {
			this.appVersion = notNullOrUndefined(this.currentPairing.appVersion) ? this.currentPairing.appVersion : 10000;
			if (this.currentPairing.phoneOS && this.currentPairing.phoneOS.indexOf("iOS") >= 0) {
				this.minVersion = result.minIOS;
				this.currentVersion = result.currentIOS;
			} else if (this.currentPairing.phoneOS && this.currentPairing.phoneOS.indexOf("Android") >= 0) {
				this.minVersion = result.minAndroid;
				this.currentVersion = result.currentAndroid;
			}

			if (this.appVersion < this.currentVersion || this.appVersion < this.minVersion) {
				this.setShowUpdateInfo(true);
				this.setOpen(true);
			} else {
				this.setShowUpdateInfo(false);
			}
		}
	}

	@computed
	public get maxPairingAge() {
		return this.systemDefaultsStore.systemDefaults.homeReadingMaxPairingAge;
	}

	@computed
	public get qrCodeSource() {
		let qrCodeSource = "/POW3/Study/QRCode?size=4&code=https://homer.pacsonweb.com/";
		qrCodeSource += this.browserKey + "/";
		qrCodeSource += this.pageId + "/";
		qrCodeSource += this.currentWebSocketSecure + "/";
		qrCodeSource += this.currentWebSocketAddress + "/";
		qrCodeSource += this.currentWebSocketPort;
		return qrCodeSource;
	}

	@action
	private setShowUpdateInfo(value: boolean) {
		this.showUpdateInfo = value;
	}

	@action
	private setPairRequestData(data: any) {
		this.pairRequestData = data;
	}

	//B -> P
	@action
	public async acceptPairingRequest(): Promise<void> {
		if (!this._currentUser) return;

		if (this.newRequestTimeout) {
			clearTimeout(this.newRequestTimeout);
		}
		if (this.newRequestInterval) {
			clearInterval(this.newRequestInterval);
		}

		this.pairRequestData.type = "return";
		this.pairRequestData.result = true;

		const returnData = {
			browserKey: this.pairRequestData.browserKey,
			pguid: this.pairRequestData.pguid,
			result: true,
		};

		const newPairing: ITblHomerPairing = {
			tblUser_UID: this._currentUser.id,
			browserKey: this.browserKey,
			pGuid: this.pairRequestData.pguid,
			pairCode: this.pairRequestData.pairCode,
			browserAgent: "",
			phoneOS: this.pairRequestData.phoneInfo.phoneOS,
			phoneType: this.pairRequestData.phoneInfo.phoneType,
			phoneName: this.pairRequestData.phoneInfo.phoneName,
			phoneID: this.pairRequestData.phoneInfo.phoneID,
			creationTime: "",
			appVersion: typeof this.pairRequestData.phoneInfo.version !== "undefined" && this.pairRequestData.phoneInfo.version != null ? this.pairRequestData.phoneInfo.version : 10000,
		};

		const result = await this.homeReadingFetcher.addNewPairing(newPairing);
		if (result.allOk) {
			this.setCurrentPairing(newPairing);
			if (this.dobcoSocket) {
				this.dobcoSocket.send("pairRequest", returnData, undefined, "return", this.pairRequestData.original.guid);
			}
			this.setActiveInfoSection("connection");
			this.checkIfPhoneConnected();
		}

		this.setPairRequestData(null);
	}

	@computed
	public get tblUserUid(): number | null {
		return this._currentUser?.id ?? null;
	}

	//B -> P
	@action
	public denyPairingRequest(): void {
		if (!this._currentUser) return;

		if (this.newRequestTimeout) {
			clearTimeout(this.newRequestTimeout);
		}
		if (this.newRequestInterval) {
			clearInterval(this.newRequestInterval);
		}

		if (this.pairRequestData == null) {
			return;
		}

		this.pairRequestData.type = "return";
		this.pairRequestData.result = false;

		const returnData = {
			browserKey: this.pairRequestData.browserKey,
			pguid: this.pairRequestData.pguid,
			result: false,
		};

		if (this.dobcoSocket) {
			this.dobcoSocket.send("pairRequest", returnData, undefined, "return", this.pairRequestData.original.guid);
		}
		this.renewPageId();
		this.setPairRequestData(null);
		this.setActiveInfoSection("qr");
	}

	//P -> B
	@action
	private async unpairConnectionFromPhone(): Promise<void> {
		const self = this;
		if (this.currentPairing == null) return;
		const result = await this.homeReadingFetcher.removePairing(this.currentPairing, this.browserKey);
		if (result.allOk) {
			const passObject = {
				bAuth: {
					browserKey: this.browserKey,
					pguid: this.currentPairing.pGuid,
					pairCode: this.currentPairing.pairCode,
				},
			};
			this.setCurrentPairing(null);

			if (this.dobcoSocket) {
				this.dobcoSocket.send("isPhoneConnected", passObject, function (data) {
					if (typeof data.exception !== "undefined") self.setPhoneConnectedStatus(false);
				});
			}

			this.renewPageId();
			this.setActiveInfoSection("qr");
		}
	}

	//P -> B
	@action
	private async pairConnectionFromPhone(): Promise<void> {
		if (this.newRequestTimeout) {
			clearTimeout(this.newRequestTimeout);
		}
		if (this.newRequestInterval) {
			clearInterval(this.newRequestInterval);
		}

		await this.getPairings();

		if (!document.hidden && document.hasFocus()) {
			if (this.isAppViewer) {
				this.trigger("setPageActive", undefined);
			} else {
				this.setPageActive();
			}
		}
	}

	@action
	private setPageActive() {
		const self = this;
		if (this.currentPairing != null && !this.isAppViewer && this.dobcoSocket) {
			this.dobcoSocket.send("setPageActive", { browserKey: this.browserKey, pageID: this.pageId }, function (data) {
				self.setEmptyStudyActive();
			});
		}
	}

	//B -> P
	@action
	public async unpairConnectionFromBrowser(): Promise<void> {
		if (!this.currentPairing) return;
		const result = await this.homeReadingFetcher.removePairing(this.currentPairing, this.browserKey);
		if (result.allOk) {
			const passObject = {
				bAuth: {
					browserKey: this.browserKey,
					pguid: this.currentPairing.pGuid,
					pairCode: this.currentPairing.pairCode,
				},
			};

			if (this.dobcoSocket) {
				this.dobcoSocket.send("unpair", passObject, function (data) {});
			}

			this.setCurrentPairing(null);
			this.renewPageId();
			this.setActiveInfoSection("qr");
			this.setPhoneConnectedStatus(false);
			this.setPairRequestData(null);
			if (this.checkConnectionInterval) {
				clearInterval(this.checkConnectionInterval);
			}
		}
	}

	//B -> P
	@action
	private renewPageId(): void {
		this.pageId = newGuid();
		if (this.dobcoSocket) {
			this.dobcoSocket.pageId = this.pageId;
			this.dobcoSocket.send("renewPageID", { browserKey: this.browserKey, pageID: this.pageId });
		}
	}

	//B -> P
	@action
	public changeLanguageFromBrowser(language: string): void {
		if (this.dobcoSocket && this.currentPairing) {
			this.dobcoSocket.send(
				"languageChangedFromBrowser",
				{
					bAuth: {
						browserKey: this.browserKey,
						pguid: this.currentPairing.pGuid,
						pairCode: this.currentPairing.pairCode,
					},
					pageID: this.pageId,
					language: language,
				},
				undefined,
			);
			this.setDefaultLanguage(language);
		}
	}

	@action
	private changeLanguageFromPhone(data: any) {
		this.setDefaultLanguage(data.language);
	}

	@action
	private setDefaultLanguage(language: string) {
		this.defaultLanguage = language;
	}

	@action
	private initialSetPageActive(): void {
		const self = this;
		if (this.currentPairing != null && !document.hidden && document.hasFocus() && !this.isAppViewer && self.dobcoSocket) {
			self.dobcoSocket.send("setPageActive", { browserKey: self.browserKey, pageID: self.pageId }, function (data) {
				self.setEmptyStudyActive();
			});
		}
	}

	@action
	private setEmptyStudyActive(): void {
		if (this.currentPairing != null && this.dobcoSocket) {
			const params = {
				bAuth: {
					browserKey: this.browserKey,
					pguid: this.currentPairing.pGuid,
					pairCode: this.currentPairing.pairCode,
				},
				pageID: this.pageId,
				study: null,
				report: null,
				cursorStart: -1,
				cursorEnd: -1,
			};

			this.dobcoSocket.send("setStudy", params, function (data) {});
		}
	}

	@computed
	private get isAppViewer(): boolean {
		return (document?.body?.classList?.value?.indexOf("study") ?? -1) >= 0;
	}

	@action
	public on(event: IHomeReadingInterfaceEvents, callback: Function) {
		if (typeof this.events[event] == "undefined") {
			this.events[event] = [];
		}
		this.events[event].push(callback);
	}

	@action
	private newPairingRequest(data: any) {
		if (this.pairRequestData != null) {
			return; //Ignoring second pairing from other device, when first device is waiting response on pairing scan
		}

		if (this.currentPairing != null) {
			this.denyPairingRequest();
			return;
		}

		this.setPairRequestData(data);
		this.showPairingRequestInfo();
	}

	@action
	private showPairingRequestInfo() {
		if (this.pairRequestData == null) return;

		if (this.newRequestInterval != null) {
			clearInterval(this.newRequestInterval);
		}
		if (this.newRequestTimeout != null) {
			clearTimeout(this.newRequestTimeout);
		}

		this.newRequestTimer = this.newRequestTimeoutInSeconds;

		this.newRequestTimeout = setTimeout(() => {
			this.denyPairingRequest();
			this.setActiveInfoSection("qr");
		}, this.newRequestTimeoutInSeconds * 1000);

		this.newRequestInterval = setInterval(() => {
			this.newRequestTimer -= 1;
			if (this.newRequestTimer < 0 && this.newRequestInterval != null) {
				clearInterval(this.newRequestInterval);
			}
		}, 1000);

		this.setActiveInfoSection("request");
		this.setOpen(true);
	}

	@action
	public setOpen(open: boolean) {
		this.open = open;
	}

	private trigger(event: IHomeReadingInterfaceEvents, data) {
		for (let i in this.events[event]) this.events[event][i](data);
	}
}
